import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Send, SquarePen, Trash2, ThumbsUp, ThumbsDown, Bot, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import './PublicChatInterface.css';

const ChatInterface = ({ contextData, contextLoading, uploadedFiles, webpages }) => {
  const { conversationId: urlConversationId } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [correctionText, setCorrectionText] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState({});
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [submittedFeedback, setSubmittedFeedback] = useState(new Set());
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  // Debug logging - only when props actually change
  useEffect(() => {
    console.log('ChatInterface received contextData:', contextData);
    console.log('ChatInterface received uploadedFiles:', uploadedFiles);
    console.log('UploadedFiles length:', uploadedFiles?.length);
  }, [contextData, uploadedFiles]);

  // Function to create combined context from text input only (not uploaded files)
  // For regular chat, we rely on the vector store for file retrieval
  const getCombinedContext = () => {
    // Only include freeform text context, not uploaded files
    // The vector store will handle retrieving relevant file content
    return contextData || '';
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation from URL parameter
  useEffect(() => {
    if (urlConversationId && urlConversationId !== conversationId) {
      console.log('Loading conversation from URL:', urlConversationId);
      loadConversation(parseInt(urlConversationId));
    }
  }, [urlConversationId]);

  // Load a specific conversation
  const loadConversation = async (conversationIdToLoad) => {
    try {
      const response = await fetch(`/api/agents/conversations/${conversationIdToLoad}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.conversation?.messages) {
          setConversationId(conversationIdToLoad);
          
          // Load feedback for this conversation
          const conversationFeedback = await loadConversationFeedback(conversationIdToLoad);
          
          const mappedMessages = data.conversation.messages.map(msg => ({
            id: Date.now() + Math.random(), // Temporary UI ID
            dbId: msg.id, // Real database ID
            type: msg.role === 'user' ? 'user' : 'bot',
            content: msg.content,
            timestamp: new Date(msg.created_at)
          }));
          
          setMessages(mappedMessages);
          
          // Set the feedback state for this conversation
          const feedbackState = {};
          mappedMessages.forEach(msg => {
            if (msg.type === 'bot' && conversationFeedback[msg.dbId]) {
              const feedback = conversationFeedback[msg.dbId];
              if (feedback.rating > 0) {
                feedbackState[msg.id] = 'thumbs_up';
              } else if (feedback.rating < 0) {
                feedbackState[msg.id] = 'thumbs_down';
              }
            }
          });
          setSelectedFeedback(feedbackState);
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Load feedback count
  const loadFeedbackCount = async () => {
    if (!user?.agent?.id) return;
    
    try {
      const response = await fetch(`/api/agents/feedback/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedbackCount(data.length);
      }
    } catch (error) {
      console.error('Error loading feedback count:', error);
    }
  };

  // Load feedback for a specific conversation
  const loadConversationFeedback = async (conversationId) => {
    if (!user?.agent?.id) return {};
    
    try {
      const response = await fetch(`/api/agents/feedback/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter feedback for this conversation and create a map by message ID
        const conversationFeedback = {};
        data.forEach(feedback => {
          if (feedback.conversation_id === conversationId) {
            conversationFeedback[feedback.message_id] = {
              rating: feedback.rating,
              feedbackType: feedback.feedback_type
            };
          }
        });
        return conversationFeedback;
      }
    } catch (error) {
      console.error('Error loading conversation feedback:', error);
    }
    return {};
  };

  // Listen for custom events from MainLayout
  useEffect(() => {
    const handleLoadConversation = (event) => {
      console.log('ChatInterface received loadConversation event:', event.detail);
      loadConversation(event.detail.conversationId);
    };

    const handleStartNewChat = () => {
      console.log('ChatInterface received startNewChat event');
      startNewConversation();
    };

    window.addEventListener('loadConversation', handleLoadConversation);
    window.addEventListener('startNewChat', handleStartNewChat);

    return () => {
      window.removeEventListener('loadConversation', handleLoadConversation);
      window.removeEventListener('startNewChat', handleStartNewChat);
    };
  }, [conversationId]);

  // Feedback handlers
  const handleThumbsUp = async (message) => {
    // Prevent double submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }

    if (!user?.agent?.id) {
      console.error('No agent ID available for feedback');
      return;
    }

    // Wait for message to have a dbId (for new messages)
    if (!message.dbId) {
      console.log('Message not yet saved to database, waiting...');
      // Wait a bit and try again
      setTimeout(() => {
        const updatedMessage = messages.find(m => m.id === message.id);
        if (updatedMessage?.dbId) {
          handleThumbsUp(updatedMessage);
        }
      }, 1000);
      return;
    }

    // Create a unique key for this feedback submission
    const feedbackKey = `${message.dbId}-thumbs_up`;
    
    // Check if this feedback has already been submitted
    if (submittedFeedback.has(feedbackKey)) {
      console.log('Thumbs up already submitted for this message');
      return;
    }

    // Toggle selected state
    const messageId = message.id;
    const currentState = selectedFeedback[messageId];
    const isSelected = currentState === 'thumbs_up';
    
    if (isSelected) {
      // Deselect
      setSelectedFeedback(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
    } else {
      // Select thumbs up
      setSelectedFeedback(prev => ({
        ...prev,
        [messageId]: 'thumbs_up'
      }));
    }

    // Set loading state
    setIsSubmittingFeedback(true);

    try {
      const response = await fetch(`/api/agents/${user.agent.id}/messages/${message.dbId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          feedback: isSelected ? 'down' : 'up'
        })
      });
      
      console.log('Thumbs up feedback payload:', {
        agentId: user.agent.id,
        conversationId: conversationId,
        messageId: message.dbId,
        userQuestion: messages[messages.indexOf(message) - 1]?.content || 'Unknown question'
      });

      if (response.ok) {
        console.log('Thumbs up feedback submitted');
        // Add to submitted feedback set to prevent duplicates
        setSubmittedFeedback(prev => new Set(prev).add(feedbackKey));
        // Update feedback count
        loadFeedbackCount();
      } else {
        console.error('Failed to submit thumbs up feedback:', response.status);
      }
    } catch (error) {
      console.error('Error submitting thumbs up feedback:', error);
    } finally {
      // Always clear loading state
      setIsSubmittingFeedback(false);
    }
  };

  const handleThumbsDown = (message) => {
    // Prevent interaction during feedback submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }

    // Toggle selected state
    const messageId = message.id;
    const currentState = selectedFeedback[messageId];
    const isSelected = currentState === 'thumbs_down';
    
    if (isSelected) {
      // Deselect
      setSelectedFeedback(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
    } else {
      // Select thumbs down and open correction modal
      setSelectedFeedback(prev => ({
        ...prev,
        [messageId]: 'thumbs_down'
      }));
      setSelectedMessage(message);
      setShowCorrectionModal(true);
    }
  };

  const handleSubmitCorrection = async () => {
    console.log('Submit correction clicked');
    console.log('correctionText:', correctionText);
    console.log('selectedMessage:', selectedMessage);
    console.log('user:', user);
    console.log('conversationId:', conversationId);
    
    // Prevent double submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }
    
    if (!correctionText.trim() || !selectedMessage) {
      console.log('Missing correction text or selected message');
      return;
    }
    
    if (!user?.agent?.id) {
      console.error('No agent ID available for feedback');
      return;
    }

    // Wait for message to have a dbId (for new messages)
    if (!selectedMessage.dbId) {
      console.log('Message not yet saved to database, waiting...');
      // Wait a bit and try again
      setTimeout(() => {
        const updatedMessage = messages.find(m => m.id === selectedMessage.id);
        if (updatedMessage?.dbId) {
          setSelectedMessage(updatedMessage);
          handleSubmitCorrection();
        }
      }, 1000);
      return;
    }

    // Create a unique key for this feedback submission
    const feedbackKey = `${selectedMessage.dbId}-${correctionText.trim()}`;
    
    // Check if this feedback has already been submitted
    if (submittedFeedback.has(feedbackKey)) {
      console.log('Feedback already submitted for this message');
      return;
    }

    // Add to submitted feedback set to prevent duplicates
    setSubmittedFeedback(prev => new Set(prev).add(feedbackKey));

    console.log('Submitting correction:', {
      agentId: user.agent.id,
      conversationId: conversationId,
      messageId: selectedMessage.dbId || selectedMessage.id,
      selectedMessage: selectedMessage,
      correctionText: correctionText
    });

    // Set loading state
    setIsSubmittingFeedback(true);

    try {
      const response = await fetch(`/api/agents/${user.agent.id}/messages/${selectedMessage.dbId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          feedback: 'down'
        })
      });

      if (response.ok) {
        console.log('Correction feedback submitted');
        setShowCorrectionModal(false);
        setSelectedMessage(null);
        setCorrectionText('');
        // Update feedback count
        loadFeedbackCount();
      } else {
        console.error('Failed to submit correction feedback:', response.status);
      }
    } catch (error) {
      console.error('Error submitting correction feedback:', error);
    } finally {
      // Always clear loading state
      setIsSubmittingFeedback(false);
    }
  };

  const handleCloseModal = () => {
    setShowCorrectionModal(false);
    setSelectedMessage(null);
    setCorrectionText('');
    setIsSubmittingFeedback(false);
  };


  // Load existing conversation history when component mounts
  useEffect(() => {
    const loadConversationHistory = async () => {
      if (!user?.agent?.id) return;
      
      // Load feedback count
      loadFeedbackCount();
      
      try {
        // Get the most recent conversation
        const response = await fetch(`/api/agents/conversations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.conversations && data.conversations.length > 0) {
            // Filter out external conversations (where user_id is not null)
            const ownerConversations = data.conversations.filter(conv => !conv.user_id);
            
            if (ownerConversations.length > 0) {
              const latestConversation = ownerConversations[0];
              setConversationId(latestConversation.id);
              
              // Load messages for this conversation
              const messagesResponse = await fetch(`/api/agents/conversations/${latestConversation.id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              
              if (messagesResponse.ok) {
                const messagesData = await messagesResponse.json();
                if (messagesData.conversation?.messages) {
                  setMessages(messagesData.conversation.messages.map(msg => ({
                    id: Date.now() + Math.random(), // Temporary UI ID
                    dbId: msg.id, // Real database ID
                    type: msg.role === 'user' ? 'user' : 'bot',
                    content: msg.content,
                    timestamp: new Date(msg.created_at)
                  })));
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading conversation history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadConversationHistory();
  }, [user]);

  // Removed redundant system message logic - now handled in startNewConversation


  const startNewConversation = async () => {
    // Clear current conversation
    setMessages([]);
    setConversationId(null);
    setInputMessage('');
    setIsLoading(false);
    
    // Add the system message for the new conversation
    setMessages([{
      id: Date.now(),
      type: 'system',
      content: 'Your AI agent is ready! I\'ve learned from your uploaded context and files and can now help answer questions based on your expertise.',
      timestamp: new Date()
    }]);
  };

  const deleteCurrentConversation = async () => {
    if (!conversationId) return;
    
    try {
      const response = await fetch(`/api/agents/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        // Clear current conversation
        setMessages([]);
        setConversationId(null);
        setInputMessage('');
        setIsLoading(false);
        
        // Dispatch event to refresh conversation list
        window.dispatchEvent(new CustomEvent('conversationDeleted', { 
          detail: { conversationId } 
        }));
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };


  const addMessageToConversation = async (role, content, forceConversationId = null) => {
    try {
      console.log('=== addMessageToConversation called ===');
      console.log('Role:', role);
      console.log('Content:', content);
      console.log('Force conversation ID:', forceConversationId);
      console.log('Force conversation ID type:', typeof forceConversationId);
      console.log('Current conversation ID state:', conversationId);
      console.log('Current conversation ID state type:', typeof conversationId);
      
      let currentConvId = forceConversationId || conversationId;
      console.log('Using conversation ID:', currentConvId);
      console.log('Using conversation ID type:', typeof currentConvId);
      
      // Convert to string for comparison
      const currentConvIdStr = String(currentConvId);
      
      // If no conversation exists, create one first
      if (!currentConvId || currentConvIdStr.startsWith('temp-')) {
        const createResponse = await fetch('/api/agents/conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: role === 'user' ? 
              (content.length > 50 ? content.substring(0, 50) + '...' : content) : 
              'New Conversation'
          })
        });
        
        if (createResponse.ok) {
          const createData = await createResponse.json();
          currentConvId = createData.conversation.id;
          setConversationId(currentConvId);
          // Dispatch event to refresh conversation list
          window.dispatchEvent(new CustomEvent('conversationCreated', { 
            detail: { conversationId: currentConvId } 
          }));
        } else {
          throw new Error('Failed to create conversation');
        }
      }
      
      // Now add the message to the conversation
      const response = await fetch(`/api/agents/conversations/${currentConvId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          role,
          content
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Message saved successfully:', data);
        return {
          messageId: data.message.id,
          conversationId: data.message.conversation_id
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save message:', response.status, errorData);
        throw new Error(`Failed to save message: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  };

  const handleSendMessage = async () => {
    console.log('handleSendMessage')
    if (!inputMessage.trim()) return;
  
    const combinedContext = getCombinedContext();
    const hasTextContext = contextData && contextData.trim().length > 0;
    const hasFileContext = uploadedFiles && uploadedFiles.length > 0;
    const hasWebpageContext = webpages && webpages.length > 0;
  
    if (!hasTextContext && !hasFileContext && !hasWebpageContext) {
      alert('Please add some context (text, files, or webpages) before chatting with your agent.');
      return;
    }
  
    const userMessage = {
      id: Date.now(), // Temporary ID for UI
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    console.log('userMessage', inputMessage);
    console.log('User:', user);
    console.log('Token:', localStorage.getItem('token'));

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Add message to conversation (this will create the conversation if it doesn't exist)
    const userMessageResult = await addMessageToConversation('user', inputMessage);
    
    if (!userMessageResult) {
      console.error('Failed to save user message, aborting bot response');
      setIsLoading(false);
      return;
    }
    
    const userMessageId = userMessageResult.messageId;
    const currentConversationId = userMessageResult.conversationId;
    
    console.log('User message saved successfully:', { userMessageId, currentConversationId });
    
    // Update the user message with the real database ID
    if (userMessageId) {
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id ? { ...msg, dbId: userMessageId } : msg
      ));
    }
  
    try {
      console.log('trying to fetching /api/agents/chat')
      console.log('Request body:', {
        context: combinedContext,
        messages: [
          ...messages.slice(-10).map(m => ({ 
            role: m.type === 'user' ? 'user' : 'assistant', 
            content: m.content 
          })),
          { role: 'user', content: inputMessage }
        ],
        conversationId: conversationId
      });
      
      const response = await fetch('/api/agents/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          context: combinedContext,
          messages: [
            ...messages.slice(-10).map(m => ({ 
              role: m.type === 'bot' ? 'assistant' : m.type === 'user' ? 'user' : 'system', 
              content: m.content 
            })),
            { role: 'user', content: inputMessage }
          ],
          conversationId: conversationId
        })
      });
  
      const data = await response.json();
      console.log('Chat API response:', data);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${data.error || 'Unknown error'}`);
      }
      
      const botMessage = {
        id: Date.now() + 1, // Temporary ID for UI
        type: 'bot',
        content: data.response || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Save bot message to the same conversation as the user message
      console.log('=== BOT MESSAGE SAVING START ===');
      console.log('Current conversation ID:', currentConversationId);
      console.log('Current conversation ID type:', typeof currentConversationId);
      console.log('Bot message content:', botMessage.content);
      
      const botMessageResult = await addMessageToConversation('assistant', botMessage.content, currentConversationId);
      
      if (botMessageResult) {
        const botMessageId = botMessageResult.messageId;
        console.log('Bot message result:', botMessageResult);
        console.log('Bot message saved with ID:', botMessageId);
        console.log('=== BOT MESSAGE SAVING END ===');
        
        // Update the bot message with the real database ID
        if (botMessageId) {
          setMessages(prev => prev.map(msg => 
            msg.id === botMessage.id ? { ...msg, dbId: botMessageId } : msg
          ));
        }
      } else {
        console.error('Failed to save bot message');
        console.log('=== BOT MESSAGE SAVING END (FAILED) ===');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoadingHistory) {
    return (
      <div className="chat-interface">
        <div className="loading-message">
          <p>Loading your conversation history...</p>
        </div>
      </div>
    );
  }

  // Show loading spinner while context is being loaded
  if (contextLoading) {
    return (
      <div className="chat-interface">
        <div className="no-context">
          <div className="loading-spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h2>Loading Context...</h2>
          <p>Please wait while we load your agent's context.</p>
        </div>
      </div>
    );
  }

  // Check if we have either text context OR uploaded files OR webpages
  const hasContext = (contextData && contextData.trim()) || (uploadedFiles && uploadedFiles.length > 0) || (webpages && webpages.length > 0);
  
  if (!hasContext) {
    return (
      <div className="chat-interface">
        <div className="no-context">
          <AlertCircle className="alert-icon" />
          <h2>No Context Available</h2>
          <p>Please upload some context or paste text in the "Upload Context" tab first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-chat-interface" data-theme="dark">
      {/* Using: Context Bar */}
      <div className="context-bar">
        <div className="context-info">
          <span className="context-label" style={{ color: 'var(--text-primary)' }}>Using:</span>
          {contextData && contextData.trim() && <span className="context-item">Text ({contextData.length} chars)</span>}
          {uploadedFiles && uploadedFiles.length > 0 && (
            <span className="context-item">Files ({uploadedFiles.length})</span>
          )}
          {webpages && webpages.length > 0 && (
            <span className="context-item">Webpages ({webpages.length})</span>
          )}
          {feedbackCount > 0 && (
            <span className="context-item">Feedback ({feedbackCount})</span>
          )}
          {(!contextData || !contextData.trim()) && (!uploadedFiles || uploadedFiles.length === 0) && (!webpages || webpages.length === 0) && feedbackCount === 0 && (
            <span className="context-item no-context">No context available</span>
          )}
        </div>
        <div className="context-controls">
          <button className="context-btn new-chat" onClick={startNewConversation}>
            <SquarePen size={16} />
          </button>
          {conversationId && (
            <button className="context-btn delete" onClick={deleteCurrentConversation}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Messages List */}
      <div className="messages-list">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type === 'user' ? 'user' : 'assistant'}`}>
            <div className="message-avatar">
              {message.type === 'user' ? (
                <div className="user-avatar-mockup">O</div>
              ) : <Bot size={20} />}
            </div>
            <div className="message-text">
              <div className="message-sender-info">
                <span className="message-sender">
                  {message.type === 'user' ? 'Owner' : 'Agent'}
                </span>
                {message.type === 'bot' && (
                  <div className="feedback-icons">
                    <ThumbsUp 
                      className={`feedback-icon thumbs-up ${selectedFeedback[message.id] === 'thumbs_up' ? 'selected' : ''}`}
                      onClick={() => handleThumbsUp(message)}
                      size={18}
                    />
                    <ThumbsDown 
                      className={`feedback-icon thumbs-down ${selectedFeedback[message.id] === 'thumbs_down' ? 'selected' : ''}`}
                      onClick={() => handleThumbsDown(message)}
                      size={18}
                    />
                  </div>
                )}
              </div>
              {message.type === 'bot' ? (
                <div className="markdown-content">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-area">
        <div className="input-container">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me anything about your expertise..."
            className="message-input"
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button onClick={handleSendMessage} className="send-button">
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Correction Modal */}
      {showCorrectionModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="correction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Provide Correction</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="original-response">
                <strong>Original Response:</strong>
                <div className="response-text">{selectedMessage?.content}</div>
              </div>
              <div className="correction-input">
                <label htmlFor="correction">What should the response have been?</label>
                <textarea
                  id="correction"
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  placeholder="Enter the correct response..."
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="submit-btn" 
                onClick={(e) => {
                  console.log('Button clicked directly');
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmitCorrection();
                }}
                disabled={!correctionText.trim() || isSubmittingFeedback}
                style={{ pointerEvents: 'auto', zIndex: 1001 }}
              >
                {isSubmittingFeedback ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Correction'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
